/**
 * O período canônico — as dez opções da spec 05 §3, e a conta de cada uma.
 *
 * Ele morava dentro do componente de filtro da SPA interna, e o Portal precisava do
 * MESMO recorte (achado TIME-01): "Este mês" tem de significar a mesma coisa nas duas
 * superfícies, ou o cliente externo e a equipe interna leem números diferentes do mesmo
 * mês e ninguém descobre por quê.
 *
 * Copiar as noventa linhas para o Portal resolveria o sintoma e criaria o defeito: duas
 * contas de trimestre, divergindo na primeira correção que alguém fizesse num lado só.
 * A REGRA subiu para o domínio; o que fica em cada aplicação é o desenho do filtro, que
 * é legitimamente diferente (a interna tem um popover, o Portal tem um `<select>`).
 *
 * Nada aqui toca o relógio do navegador: `hoje` entra por parâmetro, e é assim que o
 * teste consegue congelar o dia sem congelar a máquina.
 *
 * Ele mora em `data/` — e não numa pasta própria — porque é aritmética de CALENDÁRIO, e
 * é `data/` que a regra de dinheiro (spec 01 §8.1) reconhece como tal. Fora dali, um
 * `mes - ((mes - 1) % 3)` é indistinguível de um centavo somado por float, e a regra
 * reprova com razão: ela não tem como saber o que o número significa.
 */
import {
  dataNominal,
  diasNoMes,
  primeiroDiaDoMes,
  somarDias,
  somarMeses,
  ultimoDiaDoMes,
  type DataNominal,
} from './data-nominal.js'

export type ChaveDePeriodo =
  | 'hoje'
  | 'esta_semana'
  | 'este_mes'
  | 'mes_passado'
  | 'este_trimestre'
  | 'trimestre_passado'
  | 'ytd'
  | 'ultimos_12_meses'
  | 'ano_2026'
  | 'personalizado'

export interface Periodo {
  readonly chave: ChaveDePeriodo
  readonly de: DataNominal
  readonly ate: DataNominal
}

/** As dez opções, NESTA ordem (spec 05 §3). */
export const OPCOES_DE_PERIODO: readonly { chave: ChaveDePeriodo; rotulo: string }[] = [
  { chave: 'hoje', rotulo: 'Hoje' },
  { chave: 'esta_semana', rotulo: 'Esta semana' },
  { chave: 'este_mes', rotulo: 'Este mês' },
  { chave: 'mes_passado', rotulo: 'Mês passado' },
  { chave: 'este_trimestre', rotulo: 'Este trimestre' },
  { chave: 'trimestre_passado', rotulo: 'Trimestre passado' },
  { chave: 'ytd', rotulo: 'Ano até hoje (YTD)' },
  { chave: 'ultimos_12_meses', rotulo: 'Últimos 12 meses' },
  { chave: 'ano_2026', rotulo: '2026' },
  { chave: 'personalizado', rotulo: 'Personalizado…' },
]

/*
  Mapa pelo MÊS EM TEXTO, e não um array com `Number(mes) - 1`.

  A regra de dinheiro (spec 01 §8.1) proíbe aritmética sobre `Number()` neste escopo, e
  proíbe com razão: ela não tem como distinguir "índice de mês" de "centavo somado por
  float". Em vez de abrir exceção para a regra, sai a aritmética — e o resultado é mais
  direto de ler, porque a chave é exatamente o que a data traz.
*/
const MESES_ABREVIADOS: Readonly<Record<string, string>> = {
  '01': 'jan', '02': 'fev', '03': 'mar', '04': 'abr', '05': 'mai', '06': 'jun',
  '07': 'jul', '08': 'ago', '09': 'set', '10': 'out', '11': 'nov', '12': 'dez',
}

function inicioDoTrimestre(dia: DataNominal): DataNominal {
  const mes = Number(dia.slice(5, 7))
  const primeiroMesDoTrimestre = mes - ((mes - 1) % 3)
  return dataNominal(`${dia.slice(0, 4)}-${String(primeiroMesDoTrimestre).padStart(2, '0')}-01`)
}

/** Domingo como início da semana, como o calendário brasileiro. */
function inicioDaSemana(dia: DataNominal): DataNominal {
  const diaDaSemana = new Date(`${dia}T12:00:00Z`).getUTCDay()
  return somarDias(dia, -diaDaSemana)
}

export function resolverPeriodo(chave: ChaveDePeriodo, hoje: DataNominal): Periodo {
  switch (chave) {
    case 'hoje':
      return { chave, de: hoje, ate: hoje }
    case 'esta_semana': {
      const de = inicioDaSemana(hoje)
      return { chave, de, ate: somarDias(de, 6) }
    }
    // D-77: mês-calendário COMPLETO, mesmo com hoje no meio dele.
    case 'este_mes':
      return { chave, de: primeiroDiaDoMes(hoje), ate: ultimoDiaDoMes(hoje) }
    case 'mes_passado': {
      const mesAnterior = somarMeses(primeiroDiaDoMes(hoje), -1)
      return { chave, de: mesAnterior, ate: ultimoDiaDoMes(mesAnterior) }
    }
    case 'este_trimestre': {
      const de = inicioDoTrimestre(hoje)
      return { chave, de, ate: ultimoDiaDoMes(somarMeses(de, 2)) }
    }
    case 'trimestre_passado': {
      const de = somarMeses(inicioDoTrimestre(hoje), -3)
      return { chave, de, ate: ultimoDiaDoMes(somarMeses(de, 2)) }
    }
    case 'ytd':
      return { chave, de: dataNominal(`${hoje.slice(0, 4)}-01-01`), ate: hoje }
    case 'ultimos_12_meses':
      return { chave, de: somarMeses(somarDias(hoje, 1), -12), ate: hoje }
    case 'ano_2026':
      return { chave, de: dataNominal('2026-01-01'), ate: dataNominal('2026-12-31') }
    case 'personalizado':
      return { chave, de: primeiroDiaDoMes(hoje), ate: ultimoDiaDoMes(hoje) }
  }
}


/** Casas decimais de `mesesNoRecorte` — a mesma resolução de `dv_pct`. */
const CASAS_DO_FATOR = 6n

/**
 * Quantos MESES cabem num recorte, com fração para períodos parciais.
 *
 * Existe por causa de um KPI que comparava grandezas de eixos diferentes: o faturamento
 * mínimo do cockpit vem de `fn_break_even` e é **mensal**, e o atingimento dividia o
 * faturamento do PERÍODO por ele. Num recorte de três meses, um negócio exatamente no
 * break-even lia ~300% — com a legenda "do faturamento mínimo do período" ao lado.
 * A decisão de 05/09/2026 (D-F) é escalar o mínimo pelo número de meses do recorte.
 *
 * **A fração é do mês-calendário, não de trinta dias fixos.** Cada mês tocado contribui
 * com `dias cobertos ÷ dias daquele mês`, então fevereiro inteiro vale 1 como julho
 * inteiro, e janeiro a dezembro dá exatamente 12. Uma régua de 30 dias daria 12,17 no ano,
 * e o cockpit passaria a errar por um sexto de mês todo ano — sempre para o mesmo lado.
 *
 * Devolve **string decimal**, e não `number`, porque quem a consome multiplica dinheiro
 * por ela: o fator entra em `Money.multiplicarPor`, que é `decimal.js`. Um `number` aqui
 * seria um float atravessando o caminho de um valor monetário.
 *
 * A soma acontece como fração exata em `bigint` (numerador e denominador), e só o último
 * passo arredonda. Somar `1/31 + 1/28 + …` em ponto flutuante acumularia erro em cada
 * parcela, e o resultado de doze meses deixaria de fechar em 12 redondo.
 *
 * Intervalo invertido devolve `0.000000`: quem chama decide o que fazer com ele, e esta
 * função não é o lugar de recusar um filtro.
 */
export function mesesNoRecorte(de: DataNominal, ate: DataNominal): string {
  if (de > ate) return `0.${'0'.repeat(Number(CASAS_DO_FATOR))}`

  let numerador = 0n
  let denominador = 1n
  let mes = primeiroDiaDoMes(de)

  while (mes <= ate) {
    const fimDoMes = ultimoDiaDoMes(mes)
    const inicioCoberto = mes > de ? mes : de
    const fimCoberto = fimDoMes < ate ? fimDoMes : ate
    /*
      Os dias entram como `bigint`, e não como `number` somado depois.

      A regra de dinheiro (`dv/no-dinheiro-por-float`) reprova aritmética sobre `Number()`
      neste pacote, e ela está certa mesmo aqui: ela não tem como saber que estes dois
      números são dias e não centavos, e uma exceção aberta para "é só um dia" é a mesma
      porta que deixa passar o centavo. Contar em `bigint` custa nada e fecha a porta.
    */
    const cobertos = BigInt(fimCoberto.slice(8, 10)) - BigInt(inicioCoberto.slice(8, 10)) + 1n
    const noMes = BigInt(diasNoMes(Number(mes.slice(0, 4)), Number(mes.slice(5, 7))))
    // n/d += cobertos/noMes, sem perder nada pelo caminho.
    numerador = numerador * noMes + cobertos * denominador
    denominador *= noMes
    mes = somarMeses(mes, 1)
  }

  const escala = 10n ** CASAS_DO_FATOR
  const arredondado = (numerador * escala * 2n + denominador) / (denominador * 2n)
  const inteiro = arredondado / escala
  const fracao = arredondado % escala
  return `${String(inteiro)}.${String(fracao).padStart(Number(CASAS_DO_FATOR), '0')}`
}

/**
 * Os MESES que uma apuração mensal realmente somou — ou `null` quando o recorte
 * pedido já fecha meses inteiros e não há o que acrescentar.
 *
 * Existe porque há telas que se pedem por DIA e se apuram por MÊS: a 8.6 trunca as
 * duas pontas do período à competência (`date_trunc('month', …)`) e devolve os meses
 * efetivos em `meta.competenciaDe/competenciaAte`. Enquanto o subtítulo mostrava só o
 * intervalo pedido, escolher "Hoje" rotulava com uma DATA um número que era do mês
 * inteiro — e quem lê fecha um número mensal debaixo de um rótulo de um dia.
 *
 * As competências entram por parâmetro, e não são recalculadas aqui: quem apurou foi o
 * servidor, e recontar do lado da tela criaria a segunda resposta para "quais meses
 * entraram".
 */
export function rotularMesesApurados(
  de: DataNominal, ate: DataNominal,
  competenciaDe: DataNominal, competenciaAte: DataNominal,
): string | null {
  if (de === competenciaDe && ate === ultimoDiaDoMes(competenciaAte)) return null
  const rotulo = (d: DataNominal): string => `${MESES_ABREVIADOS[d.slice(5, 7)] ?? ''} ${d.slice(0, 4)}`
  const inicio = rotulo(competenciaDe)
  const fim = rotulo(competenciaAte)
  return inicio === fim ? `${inicio} inteiro` : `${inicio} – ${fim}, meses inteiros`
}

/** "1–31 jul 2026" · "1 jan – 22 jul 2026" quando atravessa meses. */
export function rotularPeriodo(periodo: Periodo): string {
  const dia = (d: DataNominal): string => String(Number(d.slice(8, 10)))
  const mes = (d: DataNominal): string => MESES_ABREVIADOS[d.slice(5, 7)] ?? ''
  const ano = (d: DataNominal): string => d.slice(0, 4)

  if (periodo.de === periodo.ate) return `${dia(periodo.de)} ${mes(periodo.de)} ${ano(periodo.de)}`
  if (periodo.de.slice(0, 7) === periodo.ate.slice(0, 7)) {
    return `${dia(periodo.de)}–${dia(periodo.ate)} ${mes(periodo.de)} ${ano(periodo.de)}`
  }
  if (ano(periodo.de) === ano(periodo.ate)) {
    return `${dia(periodo.de)} ${mes(periodo.de)} – ${dia(periodo.ate)} ${mes(periodo.ate)} ${ano(periodo.ate)}`
  }
  return `${dia(periodo.de)} ${mes(periodo.de)} ${ano(periodo.de)} – ${dia(periodo.ate)} ${mes(periodo.ate)} ${ano(periodo.ate)}`
}
